import { useState } from "react";

import type { RackLayoutState } from "@/lib/sdtestapp/sdTestAppProductionClient";
import type { ProductionSlotRow } from "@/lib/sdtestapp/sdTestAppProductionClient";
import type { SdTestDevice } from "@/lib/sdtestapp/sdTestAppHttpClient";
import { cn } from "@/lib/utils";

import {
  BENCH_RACK_LEAD_HEIGHT,
  BENCH_RACK_LEAD_WIDTH,
  BENCH_RACK_SLOT_HEIGHT,
  BENCH_RACK_SLOT_WIDTH,
  buildBenchRackDisplayLayout,
  diamondCoilVertices,
  rectangleCoilBounds,
} from "./benchRackLayout";

const SOURCE_SLOT_MIME = "application/x-sdtestapp-source-slot";

function slotFillStroke(status?: string | null): { fill: string; stroke: string } {
  const normalized = String(status ?? "").toLowerCase();
  if (normalized.includes("pass") || normalized.includes("ready")) {
    return { fill: "#ecfdf5", stroke: "#10b981" };
  }
  if (normalized.includes("fail") || normalized.includes("error")) {
    return { fill: "#fef2f2", stroke: "#ef4444" };
  }
  if (normalized.includes("run") || normalized.includes("busy")) {
    return { fill: "#fffbeb", stroke: "#f59e0b" };
  }
  if (normalized) {
    return { fill: "#f0f9ff", stroke: "#0ea5e9" };
  }
  return { fill: "#ffffff", stroke: "#bbbbbb" };
}

function readDragSourceSlot(event: React.DragEvent): string | null {
  const fromMime = event.dataTransfer.getData(SOURCE_SLOT_MIME).trim();
  if (fromMime) return fromMime;
  return null;
}

function CoilOutline({ coilShape }: { coilShape: "rectangle" | "diamond" }): JSX.Element {
  if (coilShape === "diamond") {
    const points = diamondCoilVertices()
      .map((vertex) => `${vertex.x},${vertex.y}`)
      .join(" ");
    return (
      <polygon
        points={points}
        fill="none"
        stroke="#000000"
        strokeWidth={3}
        strokeLinejoin="round"
        strokeLinecap="round"
        pointerEvents="none"
      />
    );
  }
  const { width, height } = rectangleCoilBounds();
  return (
    <rect
      x={-width / 2}
      y={-height / 2}
      width={width}
      height={height}
      rx={120}
      ry={120}
      fill="none"
      stroke="#000000"
      strokeWidth={3}
      pointerEvents="none"
    />
  );
}

function CoilLead({
  coilShape,
  positions,
  leadAttach,
}: {
  coilShape: "rectangle" | "diamond";
  positions: Array<{ slotName: string; x: number; y: number }>;
  leadAttach?: { x: number; y: number } | null;
}): JSX.Element {
  let rightEdgeX: number;
  let leadCenterY: number;
  if (coilShape === "diamond" && leadAttach) {
    rightEdgeX = leadAttach.x;
    leadCenterY = leadAttach.y;
  } else if (coilShape === "diamond") {
    const vertices = diamondCoilVertices();
    rightEdgeX = vertices[1].x;
    leadCenterY = vertices[1].y;
  } else {
    const { width: rectangleWidth } = rectangleCoilBounds();
    rightEdgeX = rectangleWidth / 2;
    const first = positions.find((position) => position.slotName === "1");
    const last = positions.find((position) => position.slotName === String(positions.length));
    leadCenterY = first && last ? (first.y + last.y) / 2 : 0;
  }
  return (
    <rect
      x={rightEdgeX}
      y={leadCenterY - BENCH_RACK_LEAD_HEIGHT / 2}
      width={BENCH_RACK_LEAD_WIDTH}
      height={BENCH_RACK_LEAD_HEIGHT}
      fill="#000000"
      pointerEvents="none"
    />
  );
}

export function BenchRackCanvas({
  slots,
  devices,
  selectedConnectorId,
  poolOnlyConnectorIds = [],
  rackLayout,
  onSelectConnector,
  onAssignSlot,
  onLogQcIncident,
}: {
  slots: ProductionSlotRow[];
  devices: SdTestDevice[];
  selectedConnectorId: string | null;
  poolOnlyConnectorIds?: string[];
  rackLayout?: RackLayoutState | null;
  onSelectConnector: (connectorId: string) => void;
  onAssignSlot: (slotName: string, connectorId: string | null, sourceSlotName?: string | null) => void;
  onLogQcIncident?: (connectorId: string) => void;
}): JSX.Element {
  const [dragConnectorId, setDragConnectorId] = useState<string | null>(null);
  const [dragSourceSlot, setDragSourceSlot] = useState<string | null>(null);
  const layout = buildBenchRackDisplayLayout(rackLayout);
  const slotByName = new Map(slots.map((slot) => [slot.slotName, slot]));
  const poolOnly = new Set(poolOnlyConnectorIds);
  const poolDevices = devices.filter((device) => {
    const connectorId = String(device.connectorId ?? "");
    return (
      connectorId &&
      (poolOnly.size === 0 || poolOnly.has(connectorId) || !slots.some((slot) => slot.connectorId === connectorId))
    );
  });

  const finishDrag = () => {
    setDragConnectorId(null);
    setDragSourceSlot(null);
  };

  const beginDrag = (event: React.DragEvent, connectorId: string, sourceSlotName?: string | null) => {
    event.dataTransfer.setData("text/plain", connectorId);
    event.dataTransfer.effectAllowed = "move";
    if (sourceSlotName) {
      event.dataTransfer.setData(SOURCE_SLOT_MIME, sourceSlotName);
    }
    setDragConnectorId(connectorId);
    setDragSourceSlot(sourceSlotName ?? null);
  };

  const halfSlotWidth = BENCH_RACK_SLOT_WIDTH / 2;
  const halfSlotHeight = BENCH_RACK_SLOT_HEIGHT / 2;
  const viewBox = layout.viewBox;

  return (
    <div className="flex min-h-0 flex-col gap-0">
      <div className="h-[min(84vh,780px)] w-full">
        <svg
          viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
          preserveAspectRatio="xMidYMid meet"
          className="h-full w-full"
          role="img"
          aria-label={`${layout.coilShape} coil rack`}
        >
          <CoilOutline coilShape={layout.coilShape} />
          <CoilLead
            coilShape={layout.coilShape}
            positions={layout.positions}
            leadAttach={layout.leadAttach}
          />
          {layout.positions.map((position) => {
            const slot = slotByName.get(position.slotName);
            const connectorId = slot?.connectorId ?? "";
            const isDragTarget = Boolean(dragConnectorId);
            const { fill, stroke } = slotFillStroke(slot?.status);
            return (
              <g key={position.slotName}>
                <title>{slot?.tooltip || connectorId || `Slot ${position.slotName}`}</title>
                <rect
                  x={position.x - halfSlotWidth}
                  y={position.y - halfSlotHeight}
                  width={BENCH_RACK_SLOT_WIDTH}
                  height={BENCH_RACK_SLOT_HEIGHT}
                  rx={8}
                  ry={8}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={1.5}
                  strokeDasharray={connectorId ? undefined : "4 3"}
                  className={isDragTarget ? "cursor-copy" : "cursor-pointer"}
                  onClick={() => {
                    if (selectedConnectorId) {
                      onAssignSlot(position.slotName, selectedConnectorId);
                    }
                  }}
                  onDragOver={(event) => {
                    if (!dragConnectorId) return;
                    event.preventDefault();
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const droppedConnectorId = event.dataTransfer.getData("text/plain") || dragConnectorId;
                    if (!droppedConnectorId) {
                      finishDrag();
                      return;
                    }
                    const sourceSlotName = readDragSourceSlot(event) || dragSourceSlot;
                    onAssignSlot(position.slotName, droppedConnectorId, sourceSlotName);
                    finishDrag();
                  }}
                  onContextMenu={(event) => {
                    if (!connectorId || !onLogQcIncident) return;
                    event.preventDefault();
                    onLogQcIncident(connectorId);
                  }}
                />
                {connectorId ? (
                  <foreignObject
                    x={position.x - halfSlotWidth}
                    y={position.y - halfSlotHeight}
                    width={BENCH_RACK_SLOT_WIDTH}
                    height={BENCH_RACK_SLOT_HEIGHT}
                  >
                    <div
                      draggable
                      className="flex h-full w-full cursor-grab flex-col items-center justify-center px-1 active:cursor-grabbing"
                      title={`Drag ${connectorId} to another slot`}
                      onDragStart={(event) => beginDrag(event, connectorId, position.slotName)}
                      onDragEnd={finishDrag}
                    >
                      <span className="text-[11px] font-semibold leading-tight text-[#333]">{position.slotName}</span>
                      <span className="max-w-full truncate font-mono text-[9px] leading-tight text-[#666]">
                        {connectorId}
                      </span>
                    </div>
                  </foreignObject>
                ) : (
                  <text
                    x={position.x}
                    y={position.y + 4}
                    textAnchor="middle"
                    fill="#333333"
                    fontSize={12}
                    fontWeight={600}
                    style={{ pointerEvents: "none" }}
                  >
                    {position.slotName}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mt-2 shrink-0 space-y-2 border-t-2 border-dashed border-[#999] pt-3">
        <p className="text-sm font-medium text-foreground">Devices not included in test</p>
        <div className="flex flex-wrap gap-2">
          {poolDevices.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {selectedConnectorId
                ? `Click or drag ${selectedConnectorId} onto a rack slot.`
                : "Select a device, drag from the pool, or drag between occupied slots."}
            </p>
          ) : (
            poolDevices.map((device) => {
              const connectorId = String(device.connectorId ?? "");
              const selected = connectorId === selectedConnectorId;
              return (
                <button
                  key={connectorId}
                  type="button"
                  draggable
                  className={cn(
                    "min-w-[120px] rounded-lg border border-dashed border-[#bbb] px-3 py-2 text-left",
                    selected ? "bg-primary text-primary-foreground" : "bg-muted/40 hover:bg-muted",
                  )}
                  onClick={() => onSelectConnector(connectorId)}
                  onDragStart={(event) => beginDrag(event, connectorId)}
                  onDragEnd={finishDrag}
                >
                  <span className="block font-mono text-xs font-semibold">{connectorId}</span>
                  <span className={cn("block text-[10px]", selected ? "opacity-90" : "text-muted-foreground")}>
                    {String(device.status ?? "").trim() || "Connected"}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
